// Manually-maintained behaviour notes and provenance for iLOPA UI copy.
// Every "behaviour"/claim string shown in the app comes from
// src/content/ilopa_notes.csv — edit the CSV, not component code, so the
// copy stays reviewable and its data source stays documented.
//
// CSV columns: key, text, source, ui_location.
//   text        — the exact string displayed in the UI.
//   source      — where the claim comes from (table/column, doc, naming).
//   ui_location — documentation only (where the note renders + purpose);
//                 never displayed, ignored by this loader.
//
// A row with a key but an EMPTY text column is a deliberate placeholder:
// the section renders as "pending" in the UI until the exact wording is
// specified in the CSV. Removing the row hides the section entirely.
import raw from "../content/ilopa_notes.csv?raw";


import { useUiNotes } from "./api";


export interface IlopaNote {
  text: string;
  source?: string;
  /** 1 = CSV baseline; higher = UI edits stored on the backend. */
  version?: number;
}

/** True when the note exists but its wording hasn't been specified yet. */
export function isPending(n: IlopaNote): boolean {
  return n.text.trim().length === 0;
}

/** Minimal CSV parser: handles quoted fields and embedded commas/quotes. */
function parseCsv(input: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < input.length; i++) {
    const ch = input[i];
    if (inQuotes) {
      if (ch === '"') {
        if (input[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      row.push(field); field = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && input[i + 1] === "\n") i++;
      row.push(field); field = "";
      if (row.some((f) => f.length > 0)) rows.push(row);
      row = [];
    } else {
      field += ch;
    }
  }
  row.push(field);
  if (row.some((f) => f.length > 0)) rows.push(row);
  return rows;
}

const notes = new Map<string, IlopaNote>();
{
  const rows = parseCsv(raw);
  for (const r of rows.slice(1)) {  // skip header
    const [key, text, source] = r;
    // Keyed rows always load — empty text marks a pending placeholder.
    if (key) {
      notes.set(key.trim(), {
        text: (text ?? "").trim(),
        source: source?.trim() || undefined,
      });
    }
  }
}

/** Note by key, or undefined — callers hide the section when absent. */
export function ilopaNote(key: string): IlopaNote | undefined {
  return notes.get(key);
}


/**
 * Server-first note resolution: the backend serves the latest stored
 * version per key (UI edits win over the CSV baseline). When the notes API
 * is unreachable — the static demo bundle, or the backend is down — fall
 * back to the CSV baked into the app, read-only.
 */
export function useIlopaNote(key: string): {
  note?: IlopaNote;
  editable: boolean;
} {
  const q = useUiNotes();
  const server = q.data?.notes?.[key];
  if (server) {
    return {
      note: {
        text: server.text,
        source: server.source || undefined,
        version: server.version,
      },
      editable: q.data?.editable ?? false,
    };
  }
  return { note: ilopaNote(key), editable: false };
}
